import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { MessageCircle, ArrowUpRight } from "lucide-react";
import SafeImage from "@/components/SafeImage";
import { useSettings } from "@/context/SettingsContext";
import { waLink } from "@/lib/api";

export default function ProductCard({ product, index = 0 }) {
  const s = useSettings();
  const reduce = useReducedMotion();
  const wa = waLink(s, `Hello Hari Om Trading Co., I want to enquire about: ${product.name}${product.model && !product.model.startsWith("[") ? ` (Model: ${product.model})` : ""}`);

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col border border-[#D9DDE3] bg-white transition-shadow duration-300 hover:shadow-[0_16px_40px_-16px_rgba(17,17,17,0.25)]"
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link to={`/products/${product.id}`} className="relative block aspect-[4/3] overflow-hidden bg-[#202124]" tabIndex={-1} aria-hidden="true">
        <SafeImage
          src={product.image}
          alt=""
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
        />
        {product.brand ? (
          <span className="absolute left-3 top-3 bg-[#111111] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#F5B700]">
            {product.brand}
          </span>
        ) : null}
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <Link to={`/products/${product.id}`} data-testid={`product-link-${product.id}`}>
          <h3 className="font-heading text-lg font-semibold leading-snug text-[#161616] transition-colors group-hover:text-[#8a6800]">
            {product.name}
          </h3>
        </Link>
        <p className="mt-1 text-xs font-medium uppercase tracking-wide text-[#667085]">
          Model: {product.model || "[ADD MODEL NUMBER]"}
        </p>
        <p className="mt-3 text-sm font-semibold text-[#161616]" data-testid={`product-price-${product.id}`}>
          {product.mrp != null ? `MRP ₹${Number(product.mrp).toLocaleString("en-IN")}` : "Contact for Latest Price"}
        </p>
        <div className="mt-4 flex gap-2 pt-1">
          <Link
            to={`/quote?product=${encodeURIComponent(product.name)}&model=${encodeURIComponent(product.model || "")}`}
            data-testid={`product-enquire-${product.id}`}
            className="flex flex-1 items-center justify-center gap-1.5 bg-[#111111] px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#F5B700] hover:text-[#111111]"
          >
            Enquire <ArrowUpRight size={15} aria-hidden="true" />
          </Link>
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`WhatsApp enquiry for ${product.name}`}
            data-testid={`product-whatsapp-${product.id}`}
            className="flex w-11 items-center justify-center border border-[#D9DDE3] text-[#18864B] transition-colors hover:border-[#18864B] hover:bg-[#18864B] hover:text-white"
          >
            <MessageCircle size={17} aria-hidden="true" />
          </a>
        </div>
      </div>
    </motion.article>
  );
}
